/**
 * IL CATALOGO DELLE PAGINE — **derivato dal menu**, non scritto una seconda volta.
 *
 * ## Il difetto che chiude
 *
 * Questo file era un elenco a mano, e il menu (`appNav.ts`) un altro. Novantotto voci contro
 * centotre, e al 04-09-2026 le due liste erano gia' divergenti in tre modi:
 *
 *  · **35 pagine** stavano nel menu e non qui: chi le cercava con ⌘K non le trovava, e concludeva
 *    che non ci fossero — e' l'utente che si arrende, non il software che sbaglia;
 *  · **24 pagine** avevano due nomi. `/leave` era «Assenze» nel menu e «Ferie / Permessi» nella
 *    ricerca, `/projects` «Progetti» e «Commesse»;
 *  · 7 pagine esistevano solo qui, e in nessun menu.
 *
 * ⚠️ E' lo stesso difetto che `appNav.ts` racconta nella sua testa, un piano piu' su: fino al
 * 31-08-2026 l'albero del menu era scritto due volte — colonna desktop e cassetto mobile — ed
 * erano divergiti di **tredici voci**. Quello fu chiuso unificando l'albero. La stessa cosa era
 * ricapitata fra il menu e la ricerca, e per la stessa ragione non se n'era accorto nessuno: due
 * elenchi di cento righe in due file diversi non si confrontano a occhio.
 *
 * ## Come funziona adesso
 *
 * Le pagine del menu **non si elencano qui**: si derivano da `SECTIONS`, con la loro etichetta,
 * la loro icona e il gruppo in cui stanno. Una pagina nuova nel menu e' cercabile lo stesso
 * giorno, senza che nessuno se ne ricordi, e non puo' avere due nomi perche' il nome e' uno.
 *
 * Qui restano tre cose, e ognuna e' una decisione, non una copia:
 *
 *  · `SINONIMI` — le parole con cui **la gente cerca** una pagina, quando non sono quelle
 *    dell'etichetta. Ci sono finiti anche i ventiquattro vecchi nomi della ricerca: non si e'
 *    buttato via niente, «commesse» trova «Progetti» e «timesheet» trova «Rendicontazione»;
 *  · `NOME_NELLA_RICERCA` — le due pagine in cui la ricerca ha bisogno di un nome piu' specifico
 *    del menu, perche' fuori dal suo gruppo l'etichetta breve non basta a distinguerla;
 *  · `EXTRA` — le pagine che nel menu non ci sono: le **sotto-rotte** (il menu porta alla
 *    sezione, la ricerca sa scendere piu' in basso) e le sette che una voce non ce l'hanno.
 *
 * `tests/unit/navCatalogoUnico.test.ts` tiene il punto: una pagina che nasce in un elenco solo fa
 * fallire la prova.
 */
import type { ComponentType } from 'react';
import {
  IconAdjustments, IconBeach, IconBell, IconBox, IconCalendarStats, IconCalendarTime, IconChartBar, IconChecklist, IconClock, IconDeviceMobile, IconKey, IconLayoutDashboard, IconLayoutKanban, IconPlug, IconPlus, IconReceipt, IconRocket, IconShieldCheck, IconShieldHalf, IconSparkles, IconTable, IconUser, IconUsers,
  type IconProps,
} from '@tabler/icons-react';
import { SECTIONS } from '@/app/layouts/appNav';

export interface StaticRoute {
  to: string;
  label: string;
  section: string;
  icon: ComponentType<IconProps>;
  keywords?: string[];
}

/**
 * Le parole con cui si cerca una pagina, quando non sono quelle scritte sulla voce.
 *
 * ⚠️ Non sono parole a caso: sono soprattutto quelle del **sistema vecchio** e quelle che questo
 * file usava prima come etichetta. Chi digita la parola che ha in bocca e non trova niente
 * conclude che la funzione non c'e'.
 */
const SYNONYMS: Record<string, string[]> = {
  '/': ['dashboard', 'inizio'],
  '/admin/attendance-sources': ['punti', 'sorgenti', 'nfc', 'tag', 'badge', 'qr', 'timbratura', 'revoca', 'geofence'],
  '/admin/audit-log': ['log', 'sicurezza'],
  '/admin/branding': ['logo', 'colore'],
  '/admin/categories': ['Categorie'],
  '/admin/client-intake': ['import', 'caricamento', 'onboarding cliente', 'csv', 'anagrafiche'],
  '/admin/cross-tenant-access-logs': ['Accessi fra aziende', 'log', 'cross-tenant', 'audit', 'sotto-tenant'],
  '/admin/license-dashboard': ['Cruscotto licenze'],
  '/admin/license-plans': ['Piani commerciali'],
  '/admin/logs': ['log', 'errori', 'traccia', 'trace', 'traceid', 'richieste', '500', 'diagnostica', 'lento'],
  '/admin/portal-documents': ['Documenti portale clienti', 'portale', 'clienti', 'documenti', 'allegati', 'contratti'],
  '/admin/portal-pages': ['portale', 'pagine', 'su misura', 'custom', 'assegnazione', 'moduli', 'clienti'],
  '/admin/tenant-attributes': ['attributi', 'chiave', 'valore', 'configurazione', 'key value'],
  '/admin/tenant-users': ['utenti', 'aziende', 'multi-azienda', 'associazioni', 'user-tenants', 'perimetro', 'cross-tenant', 'gruppo'],
  '/admin/tenants': ['Aziende', 'tenant', 'organizzazione', 'azienda'],
  '/admin/tenants/group': ['gruppo', 'holding', 'organigramma', 'controllate', 'partecipazioni', 'quote', 'societario', 'company master'],
  '/admin/translate-structure': ['Struttura traduzioni'],
  '/articles': ['Articoli & guide', 'knowledge', 'kb'],
  /* «Invia comunicazioni» è diventato le circolari (task 2055): il nome vecchio resta qui, perché chi
     lo cerca trovi dove è finito. Il changelog è in `EXTRA`, alla voce «Note di rilascio». */
  '/circulars': ['comunicazioni', 'avvisi aziendali'],
  '/circulars/register': ['invia comunicazioni', 'comunicazioni', 'scrivi circolare', 'pubblica', 'revisione', 'changelog'],
  '/attendance': ['timbra', 'badge', 'attendance'],
  '/car-history': ['rilascio', 'consegna'],
  '/expense-reports': ['rimborso', 'spese', 'anticipo', 'scontrino', 'giustificativo', 'trasferta'],
  '/help': ['Aiuto & FAQ'],
  '/insights': ['kpi', 'analisi'],
  '/instruments-calendar': ['Prenotazioni mezzi', 'auto', 'prenota'],
  '/instruments-calendar/approvals': ['approva', 'manager', 'da approvare'],
  '/leave': ['Ferie / Permessi', 'ferie', 'permessi', 'assenze'],
  '/legals': ['Documenti legali', 'privacy', 'termini'],
  '/licensing': ['Licenze & piano'],
  '/media': ['immagini', 'file'],
  '/my-violations': ['multe', 'sanzioni', 'verbali', 'contestazione'],
  '/notes': ['note', 'post-it', 'promemoria', 'appunto'],
  '/onboarding': ['wizard', 'setup', 'configurazione iniziale', 'primo avvio'],
  '/orders': ['ordini', 'venduto', 'righe ordine', 'monte ore'],
  '/planning': ['planning', 'schedula'],
  /* «commesse» resta: è il nome con cui la pagina si cercava prima. Vedi `NotFoundPage`. */
  '/projects': ['progetti', 'progetti', 'lavori'],
  '/projects/wbs': ['wbs', 'work breakdown', 'albero', 'nodi', 'struttura progetto', 'avanzamento', 'progressi'],
  '/secretary/area': ['Area Segreteria'],
  '/smart-working': ['Smart working', 'sw', 'remote', 'home working'],
  '/tasks': ['attività', 'todo', 'agenda', 'settimana', 'cosa devo fare'],
  '/tickets': ['Help desk', 'ticket', 'supporto'],
  '/timesheet': ['Timesheet', 'ore', 'rendicontazione'],
  '/translations': ['lingue', 'i18n'],
  '/users': ['Utenti'],
  '/vehicles': ['Parco mezzi', 'auto', 'macchine', 'veicoli'],
  '/warehouse': ['giacenze', 'scorte', 'lotti', 'ubicazioni', 'carico', 'prelievo', 'inventario'],
  '/workforce': ['Workforce', 'dipendenti', 'staff'],
  '/workforce/bu': ['Struttura BU (aree e gruppi)', 'bu', 'aree', 'area', 'gruppi', 'gruppo', 'business unit', 'business area', 'organigramma', 'reparti', 'team'],
  '/workforce/hierarchy': ['gerarchia', 'organigramma', 'manager', 'responsabile', 'riporti', 'sottoposti', 'reporting line', 'sostituzioni', 'deleghe', 'vertice'],
  '/workforce/roles': ['ruoli', 'ruolo', 'mansioni', 'mansione', 'job title', 'qualifica', 'posizione', 'profilo permessi'],
  '/workforce/sites': ['sedi', 'sede', 'siti', 'sites', 'cantieri', 'uffici', 'magazzino', 'condivisione', 'sede legale', 'company master'],
};

/**
 * Dove la ricerca chiama una pagina in modo piu' specifico del menu, ed e' giusto cosi'.
 *
 * Due sole, e per lo stesso motivo: nel menu la voce sta dentro al suo gruppo e l'etichetta breve
 * basta, nella ricerca compare da sola in mezzo alle altre. «Task» accanto a «Task — Kanban» e
 * «Task — Gantt» non dice quale sia; «Sistema» da solo non dice niente.
 */
const NAME_IN_SEARCH: Record<string, string> = {
  '/tasks': 'Task — Agenda',
  '/admin/system': 'Manutenzione sistema',
};

/**
 * Le pagine che il menu non porta.
 *
 * Le **sotto-rotte** (`/projects/new`, `/tasks/kanban`) ci stanno di diritto: il menu porta alla
 * sezione e la ricerca sa scendere piu' in basso. Le altre — `/quality`, `/reporting`,
 * `/statistics`, `/notifications`, `/maeworld`, `/setup`, `/project-forecast` — sono pagine
 * **senza una voce di menu**, cioe' raggiungibili solo sapendo gia' che esistono. Non e' una
 * decisione presa: e' come le abbiamo trovate, ed e' il residuo da guardare.
 */
const EXTRA: StaticRoute[] = [
  { to: '/team-calendar', label: 'Calendario del team', section: 'HR', icon: IconCalendarStats, keywords: ['chi c\'è', 'ferie', 'colleghi', 'smart working', 'assenti', 'calendario team'] },
  { to: '/attendance/team-month', label: 'Atteso del team', section: 'HR', icon: IconCalendarStats, keywords: ['ore attese', 'turni', 'orario', 'mese', 'team', 'part-time'] },
  { to: '/insights/monthly-invoices', label: 'Fatturato mensile', section: 'Analytics', icon: IconReceipt },
  { to: '/approvals', label: 'Approvazioni', section: 'HR', icon: IconChecklist, keywords: ['approva', 'da approvare', 'ferie', 'permessi', 'smart working', 'note spese', 'scambi turno', 'abbonamenti'] },
  { to: '/leave/approvals', label: 'Approvazioni ferie/permessi', section: 'HR', icon: IconBeach, keywords: ['approva', 'manager', 'da approvare'] },
  { to: '/maeworld', label: 'MaeWorld', section: 'Analytics', icon: IconChartBar },
  /* Fuori dal menu: ci si arriva dal piede della pagina (26-10-2026). */
  { to: '/circulars/release-notes', label: 'Note di rilascio', section: 'Personale', icon: IconSparkles, keywords: ['changelog', 'novità', 'rilasci', 'versioni', 'aggiornamenti'] },
  { to: '/notifications', label: 'Notifiche', section: 'Personale', icon: IconBell, keywords: ['avvisi', 'campanella', 'notifiche', 'messaggi'] },
  { to: '/project-forecast', label: 'Forecast progetti', section: 'Operativo', icon: IconChartBar, keywords: ['previsione', 'forecast'] },
  { to: '/projects/gantt', label: 'Progetti — Gantt', section: 'Operativo', icon: IconCalendarTime, keywords: ['gantt', 'timeline'] },
  { to: '/projects/new', label: 'Nuovo progetto', section: 'Operativo', icon: IconPlus, keywords: ['crea', 'aggiungi', 'new project'] },
  { to: '/quality', label: 'Qualità', section: 'Operativo', icon: IconShieldCheck, keywords: ['quality', 'iso'] },
  { to: '/reporting', label: 'Reporting', section: 'Analytics', icon: IconReceipt, keywords: ['report', 'export'] },
  { to: '/settings/api-keys', label: 'API Keys', section: 'Account', icon: IconKey },
  { to: '/settings/devices', label: 'Dispositivi', section: 'Account', icon: IconDeviceMobile },
  { to: '/settings/integrations', label: 'Integrazioni', section: 'Account', icon: IconPlug },
  { to: '/settings/notification-preferences', label: 'Preferenze notifiche', section: 'Account', icon: IconBell },
  { to: '/settings/preferences', label: 'Preferenze', section: 'Account', icon: IconAdjustments },
  { to: '/settings/privacy', label: 'Privacy / GDPR', section: 'Account', icon: IconShieldCheck },
  { to: '/settings/profile', label: 'Profilo', section: 'Account', icon: IconUser },
  { to: '/settings/security', label: 'Sicurezza', section: 'Account', icon: IconShieldHalf, keywords: ['password', '2fa'] },
  { to: '/setup', label: 'Onboarding wizard', section: 'Admin', icon: IconRocket },
  { to: '/smart-working/approvals', label: 'Approvazioni smart working', section: 'HR', icon: IconChecklist, keywords: ['approva', 'sw', 'manager', 'da approvare'] },
  { to: '/statistics', label: 'Statistiche', section: 'Analytics', icon: IconChartBar },
  { to: '/tasks/gantt', label: 'Task — Gantt', section: 'Operativo', icon: IconCalendarTime, keywords: ['gantt', 'timeline'] },
  { to: '/tasks/list', label: 'Task — Lista', section: 'Operativo', icon: IconTable, keywords: ['elenco', 'lista', 'filtri'] },
  { to: '/tasks/kanban', label: 'Task — Kanban', section: 'Operativo', icon: IconLayoutKanban, keywords: ['board', 'kanban'] },
  { to: '/tasks/dashboard', label: 'Task — Cruscotto', section: 'Operativo', icon: IconLayoutDashboard, keywords: ['cruscotto', 'capogruppo', 'carico', 'scadenze', 'dashboard', 'capacità', 'chi è libero', 'saturazione'] },
  { to: '/timesheet/approval', label: 'Timesheet — Approvazione', section: 'Operativo', icon: IconChecklist, keywords: ['approva', 'firmare'] },
  { to: '/timesheet/by-project', label: 'Timesheet per progetto', section: 'Operativo', icon: IconClock, keywords: ['report ore'] },
  { to: '/timesheet/reasons', label: 'Causali', section: 'Operativo', icon: IconAdjustments },
  { to: '/configuration', label: 'Configurazione', section: 'Amministrazione', icon: IconAdjustments },
  { to: '/users/new', label: 'Nuovo utente', section: 'Admin', icon: IconPlus, keywords: ['crea utente', 'invita'] },
  { to: '/warehouse/attributes', label: 'Attributi articolo', section: 'Operativo', icon: IconBox, keywords: ['attributi', 'campi custom articolo', 'essenza', 'diametro', 'spessore', 'elenco chiuso'] },
  { to: '/warehouse/documents', label: 'Documenti di magazzino', section: 'Operativo', icon: IconBox, keywords: ['documenti', 'bolla', 'ddt', 'numerazione', 'annullo', 'storno', 'registrazioni'] },
  { to: '/warehouse/inventory', label: 'Inventario (conteggio)', section: 'Operativo', icon: IconBox, keywords: ['inventario', 'conteggio', 'rettifiche', 'sessione', 'contare'] },
  { to: '/warehouse/items', label: 'Articoli (magazzino)', section: 'Operativo', icon: IconBox, keywords: ['articoli', 'anagrafica articoli', 'codice articolo', 'unità di misura', 'scorta minima', 'lotti'] },
  { to: '/warehouse/locations', label: 'Ubicazioni', section: 'Operativo', icon: IconBox, keywords: ['scaffali', 'corsie', 'posti', 'ubicazione'] },
  { to: '/warehouse/lots', label: 'Lotti & certificati', section: 'Operativo', icon: IconBox, keywords: ['lotti', 'certificato', 'omologazione', 'tracciabilità', 'scadenza', 'ddt', 'fornitore'] },
  { to: '/warehouse/warehouses', label: 'Magazzini (anagrafica)', section: 'Operativo', icon: IconBox, keywords: ['sedi', 'depositi', 'stabilimento', 'anagrafica magazzino'] },
  { to: '/workforce/dashboard', label: 'Cruscotto HR', section: 'HR', icon: IconChartBar, keywords: ['dashboard', 'workforce'] },
  { to: '/workforce/external', label: 'Personale esterno', section: 'HR', icon: IconUsers },
  { to: '/workforce/site-presences', label: 'Presenze per sede', section: 'HR', icon: IconUsers, keywords: ['cantiere'] },
  { to: '/workforce/today', label: 'Chi c\'è oggi', section: 'HR', icon: IconUsers, keywords: ['presenze', 'team', 'oggi', 'coverage', 'assenti'] },
  { to: '/workforce/training', label: 'Formazione', section: 'HR', icon: IconChecklist, keywords: ['training', 'corsi'] },
];

/** Le voci del menu, con il gruppo in cui stanno: e' quel che la ricerca mostra sotto al nome. */
function dalMenu(): StaticRoute[] {
  const out: StaticRoute[] = [];
  for (const section of SECTIONS) {
    const titolo = section.title ?? 'Principale';
    for (const below of section.groups ?? []) {
      for (const v of below.items) {
        out.push({
          to: v.to,
          label: NAME_IN_SEARCH[v.to] ?? v.label,
          section: `${titolo} · ${below.title}`,
          icon: v.icon,
          keywords: SYNONYMS[v.to],
        });
      }
    }
    for (const v of section.items ?? []) {
      out.push({
        to: v.to,
        label: NAME_IN_SEARCH[v.to] ?? v.label,
        section: titolo,
        icon: v.icon,
        keywords: SYNONYMS[v.to],
      });
    }
  }
  return out;
}

/** Catalogo statico delle pagine dell'applicativo (riusato dalla gestione tenant → scheda «Rotte»). */
export const STATIC_ROUTES: StaticRoute[] = [...dalMenu(), ...EXTRA];
